import React from "react";
import { graphql } from "gatsby";
import { Container, Header } from "semantic-ui-react";
import Layout from "../components/layout";
import SEO from "../components/seo";

function ProgressCount({ count, total, status }) {
  const nbsp = "\u00A0";
  const type =
    status === "public"
      ? "Public Misconceptions"
      : status === "draft"
      ? "Draft Misconceptions"
      : "";
  const perc = Math.round((count / total) * 1000) / 10;
  const red_color = "#db2828";
  return (
    <div
      className='count-area'
      style={{
        backgroundColor: red_color + "55", // hex 55 is ~33% opacity
        borderRadius: "4px",
        padding: "0px",
      }}
    >
      <div
        className='count-bar'
        data-tooltip={`${count} of ${total} ${type} (${perc}%)`}
        style={{
          backgroundColor: red_color,
          color: "white",
          borderRadius: "4px",
          paddingTop: "4px",
          paddingBottom: "4px",
          width: (count / total) * 100 + "%",
        }}
      >
        <div className='count-label'>
          {nbsp}
          {count}
          {nbsp}/{nbsp}
          {total}
        </div>
      </div>
    </div>
  );
}

function Aspect({
  public: publicMisconceptions,
  draft: draftMisconceptions,
  name,
  check,
}) {
  return (
    <div>
      <i>{name}</i>
      <div
        style={{
          marginTop: "0.2em",
          marginBottom: "1em",
          display: "flex",
          alignItems: "center",
          gap: "1em",
        }}
      >
        <div style={{ width: "50%" }}>
          <ProgressCount
            count={publicMisconceptions.filter(check).length}
            total={publicMisconceptions.length}
            status='public'
          />
        </div>
        <div style={{ width: "50%" }}>
          <ProgressCount
            count={draftMisconceptions.filter(check).length}
            total={draftMisconceptions.length}
            status='draft'
          />
        </div>
      </div>
    </div>
  );
}

const StructurePage = args => {
  const misconceptions = args.data.misconceptions.nodes;
  const publicMisconceptions = misconceptions.filter(
    m => m.frontmatter.status === "public"
  );
  const draftMisconceptions = misconceptions.filter(
    m => m.frontmatter.status === "draft"
  );
  const hasAnyReference = pm =>
    args.data.refs.edges
      .map(e => e.node)
      .filter(refNode => refNode.frontmatter.phenomenonKinds)
      .filter(refNode =>
        refNode.frontmatter.phenomenonKinds
          .filter(pk => pk.phenomena)
          .some(pk =>
            pk.phenomena
              .filter(p => p.plMisconceptions)
              .some(p =>
                p.plMisconceptions.some(
                  plm =>
                    plm.name === pm.fields.name && plm.lang === pm.fields.pl
                )
              )
          )
      ).length > 0;
  return (
    <Layout>
      <SEO title='Structure and Content' />
      <Container text style={{ marginTop: "100px", marginBottom: "100px" }}>
        <Header as='h1' dividing>
          <Header.Content>
            Structure and Content of the Inventory
          </Header.Content>
        </Header>
        <p>
          Our inventory presents programming language misconceptions in a
          structured way. This page lists all the aspects used to extensively
          characterize a misconception.
        </p>
        <p>
          For each aspect, we indicate how many of our misconceptions include
          that aspect. We distinguish between "public" misconceptions (left
          column), which are shown by default in the inventory, and "draft"
          misconceptions (right column), which contain several useful pieces of
          information but are not yet complete.
        </p>

        <h3 style={{ marginTop: "2em" }}>
          Essential Aspects for All Misconceptions
        </h3>
        <p>
          The following are essential aspects that are necessary to include a
          misconception in our inventory:
        </p>
        <Aspect
          name='Programming Language'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.fields.pl}
        />
        <Aspect
          name='Incorrect Statement'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.frontmatter.shortDescription}
        />
        <Aspect
          name='Correct Statement'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.frontmatter.shortCorrection}
        />
        <Aspect
          name='Concepts'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.frontmatter.concepts?.length > 0}
        />
        <Aspect
          name='PL Specification'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.frontmatter.spec?.length > 0}
        />

        <h3 style={{ marginTop: "2em" }}>
          Extra Essential Aspects for Public Misconceptions
        </h3>
        <p>
          The following are key aspects that we include for all public
          misconceptions:
        </p>
        <Aspect
          name='Correction'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.fields.correction?.size > 0}
        />
        <Aspect
          name='Image'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m =>
            m.fields.image.relativePath !== "default-misconception-image.jpg"
          }
        />

        <h3 style={{ marginTop: "2em" }}>Additional Aspects</h3>
        <p>
          The following are additional aspects that can further describe a
          misconception:
        </p>
        <Aspect
          name='Origin'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.fields.origin?.size > 0}
        />
        <Aspect
          name='Symptoms'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.fields.symptoms?.size > 0}
        />
        <Aspect
          name='Value'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.fields.value?.size > 0}
        />
        <Aspect
          name='Literature References'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={hasAnyReference}
        />
        <Aspect
          name='Expressible in Notional Machine'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.frontmatter.expressibleInNms?.length > 0}
        />
        <Aspect
          name='Explainer Video'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.frontmatter.videoId}
        />
        <Aspect
          name='PyTamaro Web Activity'
          public={publicMisconceptions}
          draft={draftMisconceptions}
          check={m => m.frontmatter.pytamaroActivityVersion}
        />
      </Container>
    </Layout>
  );
};

export default StructurePage;

export const pageQuery = graphql`
  query {
    misconceptions: allMdx(
      filter: {
        fileAbsolutePath: { glob: "**/src/data/misconceptions/**/index.md" }
      }
      sort: { fields: slug, order: ASC }
    ) {
      nodes {
        fields {
          slug
          name
          pl
          image {
            relativePath
          }
          correction {
            size
          }
          origin {
            size
          }
          symptoms {
            size
          }
          value {
            size
          }
        }
        frontmatter {
          status
          concepts
          shortDescription
          shortCorrection
          observed
          pytamaroActivityVersion
          videoId
          related {
            id
            relation
          }
          inOtherPl {
            id
          }
          expressibleInNms {
            id
          }
          spec {
            id
          }
        }
      }
    }
    refs: allMdx(filter: { frontmatter: { type: { eq: "ref" } } }) {
      edges {
        node {
          frontmatter {
            phenomenonKinds {
              phenomena {
                plMisconceptions {
                  lang
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;
